import { enumDiscountScopeType } from './enumDiscountScopeType';
import { enumUnitPriceTypes } from './enumUnitPriceTypes';
import { enumVerandaPriceTypes } from './enumVerandaPriceTypes';

// Price-type groups mirror the product tabs on the checkout (order placement)
// page one-to-one, reusing the same i18n labels. A discount scoped to a group
// expands to its member price-type scopes on save; the calculator matches each
// scope on its own. labelIds are joined with " - " (e.g. "Veranda - Accessoires").
export interface PriceTypeGroup {
    key: string;
    labelIds: string[];
    scopeType: number;
    members: number[];
}

export const PRICE_TYPE_GROUPS: PriceTypeGroup[] = [
    {
        key: 'veranda',
        labelIds: ['CHECKOUT.FIELD.VERANDA'],
        scopeType: enumDiscountScopeType.VerandaPriceType,
        members: [
            enumVerandaPriceTypes.VerandaDeluxeMiniCubeClassic,
            enumVerandaPriceTypes.VerandaDeluxeMiniCubeClassicWithHelder,
            enumVerandaPriceTypes.VerandaDeluxeMiniCubeClassicWithOpaal,
            enumVerandaPriceTypes.VerandaDeluxeMiniCubeClassicWithGetint,
            enumVerandaPriceTypes.VerandaDeluxeMiniCubeClassicWithoutPoly,
            enumVerandaPriceTypes.VerandaDeluxeMiniCubeClassicWithoutGlass
        ]
    },
    {
        key: 'verandaAccessoires',
        labelIds: ['CHECKOUT.FIELD.VERANDA', 'CHECKOUT.FIELD.ACCESSOIRES'],
        scopeType: enumDiscountScopeType.UnitPriceType,
        members: [
            enumUnitPriceTypes.VerandaAccesoiresLosseOnderdelen,
            enumUnitPriceTypes.VerandaAccesoiresLedVerlichtingNormaal,
            enumUnitPriceTypes.VerandaAccesoiresLedVerlichtingDimbaar,
            enumUnitPriceTypes.VerandaAccesoiresSmartVerlichtingGoogle,
            enumUnitPriceTypes.VerandaAccesoiresSmartVerlichtingSomfy,
            enumUnitPriceTypes.VerandaAccesoiresGlasprijzen,
            enumUnitPriceTypes.VerandaAccesoiresOverigeOnderdelen
        ]
    },
    {
        key: 'spie',
        labelIds: ['CHECKOUT.FIELD.GLAZEN.SPIE'],
        scopeType: enumDiscountScopeType.UnitPriceType,
        members: [
            enumUnitPriceTypes.AccessoiresSpiePolycarbonaat,
            enumUnitPriceTypes.AccesoiresSpieGlas
        ]
    },
    {
        key: 'aluminiumRabat',
        labelIds: ['CHECKOUT.FIELD.ALUMINIUM.RABAT'],
        scopeType: enumDiscountScopeType.UnitPriceType,
        members: [enumUnitPriceTypes.AccessoiresAluminiumRabat]
    },
    {
        key: 'glazenSchuifwand',
        labelIds: ['CHECKOUT.FIELD.GLAZEN.SCHUIFWAND'],
        scopeType: enumDiscountScopeType.UnitPriceType,
        members: [
            enumUnitPriceTypes.GlazenSchuifwandHelder,
            enumUnitPriceTypes.GlazenSchuifwandGetint
        ]
    },
    {
        key: 'glazenSchuifwandAccessoires',
        labelIds: ['CHECKOUT.FIELD.GLAZEN.SCHUIFWAND', 'CHECKOUT.FIELD.ACCESSOIRES'],
        scopeType: enumDiscountScopeType.UnitPriceType,
        members: [
            enumUnitPriceTypes.GlazenSchuifwandAccessoiresHelderGlasMeerprijs,
            enumUnitPriceTypes.GlazenSchuifwandAccessoiresGetintGlasMeerprijs,
            enumUnitPriceTypes.GlazenSchuifwandAccessoiresSetprijzen,
            enumUnitPriceTypes.GlazenSchuifwandAccessoiresLosseOnderdelen,
            enumUnitPriceTypes.GlazenSchuifwandAccessoiresLosseOnderdelenMaatwerk
        ]
    },
    {
        key: 'geheel',
        labelIds: ['CHECKOUT.FIELD.GEHEEL.POLYCARBONAAT'],
        scopeType: enumDiscountScopeType.UnitPriceType,
        members: [enumUnitPriceTypes.AccessoiresGeheelPolycarbonaat]
    },
    {
        key: 'accessoires',
        labelIds: ['CHECKOUT.FIELD.SCHUIFPUI_KOZIJN'],
        scopeType: enumDiscountScopeType.UnitPriceType,
        members: [
            enumUnitPriceTypes.AccesoiresVastKozijn,
            enumUnitPriceTypes.AccesoiresAluminiumSchuifpui,
            enumUnitPriceTypes.AccesoiresGuillotineGlassysteem
        ]
    },
    {
        key: 'zonwering',
        labelIds: ['CHECKOUT.FIELD.ZONWERING'],
        scopeType: enumDiscountScopeType.VerandaPriceType,
        members: [enumVerandaPriceTypes.Zonwering]
    },
    {
        key: 'rsScreen',
        labelIds: ['CHECKOUT.FIELD.RS.SCREEN'],
        scopeType: enumDiscountScopeType.VerandaPriceType,
        members: [enumVerandaPriceTypes.RsScreen]
    },
    {
        key: 'lamellendak',
        labelIds: ['CHECKOUT.FIELD.LAMELLENDAK'],
        scopeType: enumDiscountScopeType.VerandaPriceType,
        members: [enumVerandaPriceTypes.Lamellendak]
    }
];

// The scope types that target products (vs. audience like Dealer/Customer).
export const PRODUCT_SCOPE_TYPES = [
    enumDiscountScopeType.UnitPriceType,
    enumDiscountScopeType.VerandaPriceType
];

export const isProductScopeType = (scopeType: number): boolean =>
    PRODUCT_SCOPE_TYPES.includes(scopeType);

const memberName = (scopeType: number, member: number): string => {
    const map: any = scopeType === enumDiscountScopeType.VerandaPriceType
        ? enumVerandaPriceTypes
        : enumUnitPriceTypes;
    return map[member] || String(member);
};

// The scope rows a group expands to. scopeLabel is the raw price-type name
// (the group name is reconstructed for display via matchPriceTypeGroup).
export const expandGroupToScopes = (group: PriceTypeGroup) =>
    group.members.map(member => ({
        scopeType: group.scopeType,
        scopeValue: String(member),
        scopeLabel: memberName(group.scopeType, member)
    }));

const scopeKey = (scopeType: number | string, scopeValue: number | string) =>
    `${scopeType}:${scopeValue}`;

// Does this exact set of product scopes equal a group's members?
export const matchPriceTypeGroup = (
    productScopes: Array<{ scopeType: number; scopeValue: string }>
): PriceTypeGroup | undefined =>
    PRICE_TYPE_GROUPS.find(group => {
        const want = new Set(group.members.map(m => scopeKey(group.scopeType, m)));
        return productScopes.length === want.size &&
            productScopes.every(s => want.has(scopeKey(s.scopeType, s.scopeValue)));
    });

// Collapse a mixed scope list into group rows + leftover individual product
// scopes (groups are disjoint, so the cover is unambiguous). Used to render a
// scope list where whole groups appear as a single entry.
export const collapseProductScopesToGroups = (
    productScopes: Array<{ scopeType: number; scopeValue: string }>
): { groups: PriceTypeGroup[]; loose: Array<{ scopeType: number; scopeValue: string }> } => {
    const present = new Set(productScopes.map(s => scopeKey(s.scopeType, s.scopeValue)));
    const consumed = new Set<string>();
    const groups: PriceTypeGroup[] = [];

    for (const group of PRICE_TYPE_GROUPS) {
        const keys = group.members.map(m => scopeKey(group.scopeType, m));
        if (keys.every(k => present.has(k))) {
            groups.push(group);
            keys.forEach(k => consumed.add(k));
        }
    }

    const loose = productScopes.filter(s => !consumed.has(scopeKey(s.scopeType, s.scopeValue)));
    return { groups, loose };
};
